import { logEvent } from 'firebase/analytics';
import { analytics } from './firebase';
import posthog from './posthog';

/**
 * Generic analytics interface that sends events to both PostHog and
 * Firebase Analytics.
 *
 * GDPR / consent handling:
 * - PostHog is always initialized in cookie-less memory mode.
 * - All `capture()` calls go to PostHog in anonymous mode.
 * - `identify()`, `setUserProperties()`, and Firebase `logEvent()` are only
 *   enabled when the user has explicitly opted in to analytics cookies.
 */

let currentSessionId: string | null = null;
let sessionStartTime = 0;
let analyticsConsent = false;
const useEmulators = import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true';

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    const suffix = Array.from(bytes, byte => byte.toString(36)).join('');
    return `${Date.now().toString(36)}-${suffix}`;
  }
  return Date.now().toString(36);
}

function toFirebaseEventName(event: string): string {
  // Firebase event names must be 1-40 chars, alphanumeric + underscore,
  // and must start with a letter. Normalize the PostHog-style event name.
  const normalized = event
    .replace(/\W/g, '_')
    .replace(/^\d+/, '')
    .replace(/^_+/, '');

  return normalized.slice(0, 40) || 'custom_event';
}

function toFirebaseProperties(
  properties?: Record<string, unknown>
): Record<string, unknown> | undefined {
  if (!properties) return undefined;

  const result: Record<string, unknown> = {};
  const entries = Object.entries(properties);

  // Firebase allows up to 25 event parameters
  for (const [key, value] of entries.slice(0, 25)) {
    // Firebase parameter names must be <=40 chars and contain no leading/trailing whitespace
    const safeKey = key.replace(/\W/g, '_').slice(0, 40);

    // Firebase string values should be <=100 chars
    if (typeof value === 'string') {
      result[safeKey] = value.slice(0, 100);
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      result[safeKey] = value;
    } else {
      result[safeKey] = String(value).slice(0, 100);
    }
  }

  return result;
}

function updatePosthogConfig() {
  if (useEmulators) return;

  try {
    if (!posthog.__loaded) return;

    if (analyticsConsent) {
      // Allow cross-session persistence once the user has opted in.
      posthog.set_config({ persistence: 'localStorage+cookie' });
      posthog.opt_in_capturing({ captureEventName: false });
    } else {
      // Cookie-less, memory-only tracking.
      posthog.set_config({ persistence: 'memory' });
      posthog.reset();
    }
  } catch (err) {
    console.warn('PostHog consent reconfiguration failed:', err);
  }
}

export function setAnalyticsConsent(consent: boolean) {
  if (analyticsConsent === consent) return;
  analyticsConsent = consent;
  updatePosthogConfig();
  capture('consent_changed', { consented: consent });
  console.log('[Analytics] consent set to:', consent);
}

export function capture(event: string, properties?: Record<string, unknown>) {
  if (useEmulators) return;

  try {
    posthog.capture(event, properties);
  } catch (err) {
    console.warn('PostHog capture failed:', err);
  }

  if (analyticsConsent) {
    try {
      const firebaseEvent = toFirebaseEventName(event);
      const firebaseProps = toFirebaseProperties(properties);
      logEvent(analytics, firebaseEvent, firebaseProps);
    } catch (err) {
      console.warn('Firebase Analytics capture failed:', err);
    }
  }
}

export function startSession() {
  if (currentSessionId) return currentSessionId;

  currentSessionId = generateSessionId();
  sessionStartTime = Date.now();
  capture('session_start', { session_id: currentSessionId });
  return currentSessionId;
}

export function endSession() {
  if (!currentSessionId) return;

  const duration = Date.now() - sessionStartTime;
  capture('session_end', { session_id: currentSessionId, duration });
  currentSessionId = null;
}

export function getCurrentSessionId(): string | null {
  return currentSessionId;
}

export function identify(userId: string, userProperties?: Record<string, unknown>): boolean {
  if (useEmulators || !analyticsConsent) return false;

  try {
    posthog.identify(userId, userProperties);
    return true;
  } catch (err) {
    console.warn('PostHog identify failed:', err);
    return false;
  }

  // Firebase Analytics does not have a direct identify/setUserProperties call.
  // User properties can be set via `setUserProperties(analytics, props)` if desired.
  // Keeping this a no-op for now to avoid conflicting with the existing PostHog flow.
}

export function reset() {
  if (useEmulators) return;

  try {
    posthog.reset();
  } catch (err) {
    console.warn('PostHog reset failed:', err);
  }
}

export function setUserProperties(userProperties: Record<string, unknown>) {
  if (useEmulators || !analyticsConsent) return;

  try {
    posthog.people.set(userProperties);
  } catch (err) {
    console.warn('PostHog set user properties failed:', err);
  }
}

export const analyticsClient = {
  capture,
  identify,
  reset,
  setAnalyticsConsent,
  setUserProperties,
  startSession,
  endSession,
  getCurrentSessionId,
};
